import { Select } from 'antd'
import { Moment } from 'moment'
import moment from 'moment'
import React, { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { dateTime } from '@/configs'
import { SearchEmployee } from '@/pages/Attendance/Attendance'
import { useAttendance } from '@/pages/Attendance/Attendance.hooks'
import { useEmployees } from '@/pages/Employees/Employees.hooks'
import { formatDateSendRequest } from '@/utils/date'
import { getName } from '@/utils/employee'

import {
  ButtonCustom,
  Container,
  DatePickerCustom,
  SearchDayWrapper,
  SelectEmployee,
  TitleSearch,
  WrapperSearch,
} from './styles'

type Props = {
  setDate: React.Dispatch<React.SetStateAction<string | undefined>>
  setEmployeeId: React.Dispatch<React.SetStateAction<number | undefined>>
  employeeId?: number
  date?: string
  setNotError: React.Dispatch<React.SetStateAction<boolean>>
  isNotError: boolean
  setSearchEmployee: React.Dispatch<React.SetStateAction<SearchEmployee>>
  searchEmployee: SearchEmployee
}

const FilterAttendanceByDay: FC<Props> = ({
  setDate,
  setEmployeeId,
  employeeId,
  date,
  setNotError,
  isNotError,
  searchEmployee,
  setSearchEmployee,
}) => {
  const { t } = useTranslation()

  const { getAllEmployees, allEmployeesResponse, valueSearchEmployee, getValueSearchEmployee } = useEmployees()

  const { exportExcelAttendanceByRangeDay, exportAttendanceByDayLoading } = useAttendance()

  const defaultSelectEmployee = {
    value: '-1',
    label: t('components.pages.attendanceEmployee.listReport.filter.defaultValue'),
  }

  const optionSelectEmployee = allEmployeesResponse?.map((employee) => {
    return {
      value: employee.id.toString(),
      label: getName(employee),
    }
  })

  const optionSearchGender = valueSearchEmployee?.gender.map((item) => {
    return {
      label: item,
      value: item,
    }
  })

  const optionSearchPosition = valueSearchEmployee?.position.map((item) => {
    return {
      label: item,
      value: item,
    }
  })

  const optionSearchDepartment = valueSearchEmployee?.department.map((item) => {
    return {
      label: item,
      value: item,
    }
  })

  const defaultOptionSearchGender = optionSearchGender && [defaultSelectEmployee, ...optionSearchGender]
  const defaultOptionSearchOption = optionSearchPosition && [defaultSelectEmployee, ...optionSearchPosition]
  const defaultOptionSearchDepartment = optionSearchDepartment && [defaultSelectEmployee, ...optionSearchDepartment]

  const defaultSelectEmployees = optionSelectEmployee && [defaultSelectEmployee, ...optionSelectEmployee]

  const handleExportCSV = () => {
    if (date) {
      exportExcelAttendanceByRangeDay({
        date,
        employeeId: employeeId !== Number(defaultSelectEmployee.value) ? employeeId : undefined,
        isNotError,
      })
    }
  }

  const handlePickDate = (date: Moment | null) => {
    setDate(formatDateSendRequest(date))
  }

  const handleSelectEmployee = (value: string) => {
    setEmployeeId(Number(value))
  }

  const handleSelectError = (value: boolean) => {
    setNotError(value)
  }

  const handleSelectSearchEmployee = (value: string, type: string) => {
    switch (type) {
      case 'gender':
        setSearchEmployee((pre) => ({ ...pre, gender: value }))
        break
      case 'position':
        setSearchEmployee((pre) => ({ ...pre, position: value }))
        break
      case 'department':
        setSearchEmployee((pre) => ({ ...pre, department: value }))
        break
      default:
        break
    }
  }

  useEffect(() => {
    getAllEmployees({
      gender: searchEmployee.gender !== defaultSelectEmployee.value ? searchEmployee.gender : '',
      position: searchEmployee.position !== defaultSelectEmployee.value ? searchEmployee.position : '',
      department: searchEmployee.department !== defaultSelectEmployee.value ? searchEmployee.department : '',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAllEmployees, searchEmployee])

  useEffect(() => {
    getValueSearchEmployee()
  }, [getValueSearchEmployee])

  return (
    <Container>
      <WrapperSearch>
        <SearchDayWrapper margin_bottom="0">
          <TitleSearch>{t('components.pages.attendanceEmployee.listReport.filter.filterDay')}</TitleSearch>
          <DatePickerCustom
            value={date ? moment(date) : null}
            format={dateTime.dateFormat}
            onChange={(date) => handlePickDate(date)}
            clearIcon={false}
          />
        </SearchDayWrapper>
      </WrapperSearch>
      <WrapperSearch>
        <SelectEmployee>
          <TitleSearch width="70px">{t('components.pages.attendanceEmployee.listReport.filter.gender')}</TitleSearch>
          <Select
            showSearch
            style={{ width: '100%' }}
            onChange={(value) => handleSelectSearchEmployee(value, 'gender')}
            options={defaultOptionSearchGender}
            value={searchEmployee.gender}
            defaultValue={defaultSelectEmployee.label}
          />
        </SelectEmployee>
        <SelectEmployee>
          <TitleSearch width="70px">{t('components.pages.attendanceEmployee.listReport.filter.position')}</TitleSearch>
          <Select
            showSearch
            style={{ width: '100%' }}
            onChange={(value) => handleSelectSearchEmployee(value, 'position')}
            options={defaultOptionSearchOption}
            value={searchEmployee.position}
            defaultValue={defaultSelectEmployee.label}
          />
        </SelectEmployee>
        <SelectEmployee>
          <TitleSearch width="70px">
            {t('components.pages.attendanceEmployee.listReport.filter.department')}
          </TitleSearch>
          <Select
            showSearch
            style={{ width: '100%' }}
            onChange={(value) => handleSelectSearchEmployee(value, 'department')}
            options={defaultOptionSearchDepartment}
            value={searchEmployee.department}
            defaultValue={defaultSelectEmployee.label}
          />
        </SelectEmployee>
      </WrapperSearch>
      <WrapperSearch>
        <SelectEmployee>
          <TitleSearch width="150px">
            {t('components.pages.attendanceEmployee.listReport.filter.filterEmployee')}
          </TitleSearch>
          <Select
            showSearch
            style={{ width: '100%' }}
            onChange={handleSelectEmployee}
            value={employeeId?.toString()}
            options={defaultSelectEmployees}
            defaultValue={defaultSelectEmployee.label}
            filterOption={(input, option) => (option?.label ?? '').includes(input)}
          />
        </SelectEmployee>
        <SelectEmployee>
          <TitleSearch width="150px">{t('components.pages.attendance.filter.error')}</TitleSearch>
          <Select style={{ width: '100%' }} onChange={handleSelectError} value={isNotError}>
            <Select.Option value={true}>{t('components.pages.attendance.filter.true')}</Select.Option>
            <Select.Option value={false}>{t('components.pages.attendance.filter.false')}</Select.Option>
          </Select>
        </SelectEmployee>
        <ButtonCustom onClick={handleExportCSV} loading={exportAttendanceByDayLoading}>
          {t('components.pages.attendanceEmployee.listReport.table.export')}
        </ButtonCustom>
      </WrapperSearch>
    </Container>
  )
}

FilterAttendanceByDay.displayName = 'FilterAttendanceByDay'
export default FilterAttendanceByDay
